import { z } from "zod";
import { IdSchema, IsoTimestampSchema, NonEmptyStringSchema, NonNegativeIntegerSchema, UnknownRecordSchema } from "./source.js";
/** The append-only event kinds emitted by an engine run. */
export const EngineEventKindSchema = z.enum([
    "stage.started",
    "stage.progress",
    "stage.completed",
    "stage.failed",
    "run.cancelled",
    "provider.call",
    "tool.call",
    "tool.result",
    "receipt",
    "flag",
    "warning",
    "pageFailure",
    "cost.rollup"
]);
/**
 * The wire envelope is deliberately strict. Payload is opaque to transport,
 * so the app can persist and replay fields that a newer engine adds without
 * applying a lossy projection.
 */
export const EngineEventSchema = z
    .object({
    seq: NonNegativeIntegerSchema,
    ts: IsoTimestampSchema,
    runId: IdSchema,
    stageId: NonEmptyStringSchema,
    ordinal: NonNegativeIntegerSchema,
    docId: IdSchema.optional(),
    page: NonNegativeIntegerSchema.optional(),
    kind: EngineEventKindSchema,
    payload: UnknownRecordSchema
})
    .strict();
export const EngineStageCostRollupSchema = z
    .object({
    inputTokens: NonNegativeIntegerSchema.nullable(),
    outputTokens: NonNegativeIntegerSchema.nullable(),
    completedCalls: NonNegativeIntegerSchema.nullable(),
    costGbp: z.number().nonnegative().nullable(),
    status: z.enum(["measured", "unmeasured"]).optional(),
    reason: NonEmptyStringSchema.optional(),
    artifactPath: NonEmptyStringSchema.optional(),
    artifactSha256: z.string().regex(/^[a-f0-9]{64}$/i).optional(),
    usdToGbp: z.number().positive().optional(),
    platformUsdToGbp: z.number().positive().optional(),
    conversionDiscrepancy: NonEmptyStringSchema.optional()
})
    .strict();
/**
 * Per-stage summary for the engine chain progress strip. Status is derived
 * purely from observed stage.started/stage.completed/stage.failed events on
 * the durable feed for one run — never guessed from elapsed time.
 */
export const EngineChainStageStatusSchema = z.enum([
    "pending",
    "running",
    "succeeded",
    "failed"
]);
/**
 * Contract order for the engine chain. Consumers may receive a summary whose
 * array order is stale or produced by a different transport, so presentation
 * code must use this shared order instead of trusting array position.
 */
export const ENGINE_CHAIN_STAGE_ORDER = [
    "classify",
    "scope",
    "requirements",
    "flagflip",
    "takeoff",
    "projection",
    "points",
    "panel",
    "specification",
    "installation",
    "pricing"
];
export const EngineChainStageSummarySchema = z
    .object({
    stageId: NonEmptyStringSchema,
    ordinal: NonNegativeIntegerSchema,
    status: EngineChainStageStatusSchema,
    startedAt: IsoTimestampSchema.optional(),
    completedAt: IsoTimestampSchema.optional(),
    message: NonEmptyStringSchema.optional()
})
    .strict();
/**
 * Honest tri-state summary of the engine stage chain for a tender pack's
 * active run, read from the durable engine-events feed (engine-events.v1).
 * This is deliberately NOT the legacy TenderProgressEvent stream: that
 * stream has no engine stage field and its per-doc "classify" completion
 * comes from the retired legacy classifier, which no longer runs.
 *
 * - "unavailable": the repository can't read engine event feeds, or the
 *   pack's recorded run has no readable durable feed. Never collapsed into
 *   "none" -- an unreadable run is not the same fact as "never started".
 * - "none": no engine run has been started for this pack yet.
 * - "active": a real feed was read; `stages` reflects what it contains.
 */
export const EngineChainSummarySchema = z.discriminatedUnion("state", [
    z
        .object({
        state: z.literal("unavailable"),
        reason: NonEmptyStringSchema
    })
        .strict(),
    z
        .object({
        state: z.literal("none"),
        reason: NonEmptyStringSchema
    })
        .strict(),
    z
        .object({
        state: z.literal("active"),
        runId: IdSchema,
        overallStatus: z.enum(["running", "succeeded", "failed"]),
        stages: z.array(EngineChainStageSummarySchema)
    })
        .strict()
]);
//# sourceMappingURL=engineEvents.js.map